import Accordion from "react-bootstrap/Accordion";
import T, { GetLang } from "./translations/Translations";
import { formatDate } from "@utils/DateFormatter";
import type { ActionControl, DistrictWindow } from "@/types/GlobalState";

interface WelcomeAccordionsProps {
    activeKeys: string[];
    handleAccordionSelect: (key: string | string[] | null | undefined) => void;
    intentToReturn: ActionControl | null;
    oeDistrictWindows: DistrictWindow[] | undefined;
    oeWindowsFlag: boolean;
    parentOffer: ActionControl | null;
    parentRequest: ActionControl | null;
    parentRequestRound2: ActionControl | null;
    showBeforeYouBegin?: boolean;
}

const itemStyle = { marginBottom: "10px" };

const WelcomeAccordions = ({
    activeKeys,
    handleAccordionSelect,
    intentToReturn,
    oeDistrictWindows,
    oeWindowsFlag,
    parentOffer,
    parentRequest,
    parentRequestRound2,
    showBeforeYouBegin = false
}: WelcomeAccordionsProps): JSX.Element => {
    const lang = GetLang();

    // Translations
    const welcomeImportantDates = T({ tKey: "Welcome_ImportantDates", langStr: lang });
    const welcomeFirstWindow = T({ tKey: "Welcome_FirstWindow", langStr: lang });
    const welcomeSecondWindow = T({ tKey: "Welcome_SecondWindow", langStr: lang });
    const welcomeUlLi1a = T({ tKey: "Welcome_ul_li1a", langStr: lang });
    const welcomeUlLi1b = T({ tKey: "Welcome_ul_li1b", langStr: lang });
    const welcomeUlLi2a = T({ tKey: "Welcome_ul_li2a", langStr: lang });
    const welcomeUlLi3 = T({ tKey: "Welcome_ul_li3", langStr: lang });
    const welcomeUlLi4a = T({ tKey: "Welcome_ul_li4a", langStr: lang });
    const welcomeUlLi4b = T({ tKey: "Welcome_ul_li4b", langStr: lang });
    const welcomeUlLi4c = T({ tKey: "Welcome_ul_li4c", langStr: lang });
    const welcomeUlLi5a = T({ tKey: "Welcome_ul_li5a", langStr: lang });
    const welcomeUlLi5b = T({ tKey: "Welcome_ul_li5b", langStr: lang });
    const welcomeUlLi6a = T({ tKey: "Welcome_ul_li6a", langStr: lang });
    const welcomeNotAvailable = T({ tKey: "Welcome_NotAvailable", langStr: lang });
    const welcomeGeneralInfoUl2Li4 = T({ tKey: "Welcome_GeneralInfo_ul2_li4", langStr: lang });

    return (
        <Accordion activeKey={activeKeys} alwaysOpen onSelect={handleAccordionSelect}>
            {oeDistrictWindows && oeWindowsFlag ? (
                <Accordion.Item eventKey="0" style={itemStyle}>
                    <Accordion.Header>
                        <h5>{welcomeImportantDates}</h5>
                    </Accordion.Header>
                    <Accordion.Body>
                        <p>
                            <strong>
                                {welcomeFirstWindow}:{" "}
                                <span className="grey-text">
                                    {formatDate(parentRequest?.startDate, true, true)} -{" "}
                                    {formatDate(parentRequest?.endDate, true, true)}
                                </span>
                            </strong>
                        </p>
                        <p>
                            <strong>
                                {welcomeSecondWindow}:{" "}
                                <span className="grey-text">
                                    {formatDate(parentRequestRound2?.startDate, true, true)} -{" "}
                                    {formatDate(parentRequestRound2?.endDate, true, true)}
                                </span>
                            </strong>
                        </p>
                        <ul>
                            <li className="grey-text">
                                {welcomeUlLi1a} <strong>{formatDate(parentRequest?.endDate, true, true)}</strong>{" "}
                                {welcomeUlLi1b}
                            </li>
                            <li className="grey-text">
                                {welcomeUlLi2a} <strong>{formatDate(parentRequest?.endDate, true, true)}</strong>{" "}
                            </li>
                            <li className="grey-text">{welcomeUlLi3}</li>
                            <li className="grey-text">
                                {welcomeUlLi4a} <strong>{formatDate(parentOffer?.startDate, true, true)}</strong>.{" "}
                                {welcomeUlLi4b} <strong>{formatDate(parentOffer?.startDate, true, true)}</strong>{" "}
                                {welcomeUlLi4c}
                            </li>
                            <li className="grey-text">
                                {welcomeUlLi5a} <strong>{formatDate(parentOffer?.endDate, true, true)}</strong>{" "}
                                {welcomeUlLi5b}
                            </li>
                            <li className="grey-text">
                                {welcomeUlLi6a}{" "}
                                <strong>
                                    {formatDate(parentRequestRound2?.startDate, true, true)}
                                    {" - "}
                                    {formatDate(parentRequestRound2?.endDate, true, true)}
                                </strong>
                                {"."}
                            </li>
                        </ul>
                    </Accordion.Body>
                </Accordion.Item>
            ) : (
                <Accordion.Item eventKey="0" style={itemStyle}>
                    <Accordion.Header>
                        <h5>{welcomeImportantDates}</h5>
                    </Accordion.Header>
                    <Accordion.Body>
                        <p className="grey-text">{welcomeNotAvailable}</p>
                    </Accordion.Body>
                </Accordion.Item>
            )}
            <Accordion.Item eventKey="1" style={itemStyle}>
                <Accordion.Header>
                    <h5>{T({ tKey: "Welcome_TransportationInfo", langStr: lang })}</h5>
                </Accordion.Header>
                <Accordion.Body>
                    <div className="grey-text">{T({ tKey: "Welcome_TransportationBody", langStr: lang })}</div>
                </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="2" style={itemStyle}>
                <Accordion.Header>
                    <h5>{T({ tKey: "Welcome_PreschoolInfo", langStr: lang })}</h5>
                </Accordion.Header>
                <Accordion.Body>
                    <ul>
                        <li className="grey-text">
                            <span className="highlight">{T({ tKey: "Welcome_NoSupport", langStr: lang })}</span>{" "}
                            {T({ tKey: "Welcome_PreschoolsLink", langStr: lang })}
                        </li>
                        <li className="grey-text">{T({ tKey: "Welcome_PreschoolEnrollment", langStr: lang })}</li>
                    </ul>
                </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="3" style={itemStyle}>
                <Accordion.Header>
                    <h5>{T({ tKey: "Welcome_GeneralInfo", langStr: lang })}</h5>
                </Accordion.Header>
                <Accordion.Body>
                    <ul>
                        <li className="grey-text">{T({ tKey: "Welcome_GeneralInfo_ul2_li1", langStr: lang })}</li>
                        <li className="grey-text">{T({ tKey: "Welcome_GeneralInfo_ul2_li3", langStr: lang })}</li>
                        {oeDistrictWindows && intentToReturn && (
                            <li className="grey-text">
                                {welcomeGeneralInfoUl2Li4}{" "}
                                <strong>{formatDate(intentToReturn.endDate, true, true)}</strong>.
                            </li>
                        )}
                        <li className="grey-text">{T({ tKey: "Welcome_GeneralInfo_li5", langStr: lang })}</li>
                    </ul>
                </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="4" style={itemStyle}>
                <Accordion.Header>
                    <h5>{T({ tKey: "Welcome_CharterSchoolInfo", langStr: lang })}</h5>
                </Accordion.Header>
                <Accordion.Body>{T({ tKey: "Welcome_CharterSchoolInclude", langStr: lang })}</Accordion.Body>
            </Accordion.Item>
            {showBeforeYouBegin && (
                <Accordion.Item eventKey="5" style={itemStyle}>
                    <Accordion.Header>
                        <h5>{T({ tKey: "Welcome_Before_You_Begin", langStr: lang })}</h5>
                    </Accordion.Header>
                    <Accordion.Body>{T({ tKey: "Welcome_Before_You_Begin_Body", langStr: lang })}</Accordion.Body>
                </Accordion.Item>
            )}
        </Accordion>
    );
};

export default WelcomeAccordions;
